import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Mail, Phone, MapPin, Building, Shield, Camera, Save } from "lucide-react";

export default function Profile() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Mock Profile Data
  const [profileData, setProfileData] = useState({
    fullName: "Super Admin",
    email: "admin@socialbazar.co",
    phone: "+91 98765 43210",
    role: "Administrator",
    location: "Mumbai, India",
    company: "SocialBazar Inc.",
    bio: "Lead administrator for SocialBazar platform. Managing users, analytics, and platform health.",
  });

  const handleChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handleSave = (e) => {
    e.preventDefault();
    setIsEditing(false);
    // Here you would typically send the updated data to your backend API
  };

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
      
      {/* Ambient background removed for sleek look */}

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
        <Header 
          onMenuClick={() => setIsSidebarOpen(true)} 
          title="My Profile" 
          subtitle="Manage your personal information and account settings."
        />
        
        <div className="p-4 sm:p-8 flex-1 flex flex-col w-full overflow-hidden">
          {/* Header Section */}
          <div className="mb-6 sm:mb-8">
            
            
          </div>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Left Column: Profile Card */}
            <div className="w-full lg:w-1/3 flex flex-col gap-6">
              <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl border border-slate-200 dark:border-slate-700 p-6 flex flex-col items-center text-center">
                <div className="relative mb-4 group cursor-pointer">
                  <img 
                    src="https://i.pravatar.cc/150?u=admin" 
                    alt="Profile" 
                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                  <div className="absolute inset-0 bg-slate-900/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="text-white w-8 h-8" />
                  </div>
                  <div className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full"></div>
                </div>
                
                <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">{profileData.fullName}</h2>
                <p className="text-blue-600 font-medium text-sm mt-1">{profileData.role}</p>
                
                <div className="w-full h-px bg-slate-100 my-6"></div>
                
                <div className="w-full space-y-4">
                  <div className="flex items-center gap-3 text-slate-600 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="truncate">{profileData.email}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{profileData.phone}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{profileData.location}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600 text-sm">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span>{profileData.company}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Edit Form */}
            <div className="w-full lg:w-2/3">
              <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Personal Information</h3>
                  {!isEditing && (
                    <button 
                      onClick={() => setIsEditing(true)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 px-4 py-2 rounded-xl transition-colors"
                    >
                      Edit Profile
                    </button>
                  )}
                </div>

                <form onSubmit={handleSave}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                      <input 
                        type="text" 
                        name="fullName"
                        value={profileData.fullName}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none ${
                          isEditing 
                            ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 shadow-sm" 
                            : "bg-slate-50 border-transparent text-slate-500 dark:text-slate-400"
                        }`}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                      <input 
                        type="email" 
                        name="email"
                        value={profileData.email}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none ${
                          isEditing 
                            ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 shadow-sm" 
                            : "bg-slate-50 border-transparent text-slate-500 dark:text-slate-400"
                        }`}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                      <input 
                        type="text" 
                        name="phone"
                        value={profileData.phone}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none ${
                          isEditing 
                            ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 shadow-sm" 
                            : "bg-slate-50 border-transparent text-slate-500 dark:text-slate-400"
                        }`}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Role / Designation</label>
                      <div className="relative">
                        <Shield className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                        <input 
                          type="text" 
                          name="role"
                          value={profileData.role}
                          onChange={handleChange}
                          disabled={!isEditing}
                          className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm transition-all outline-none ${
                            isEditing 
                              ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 shadow-sm" 
                              : "bg-slate-50 border-transparent text-slate-500 dark:text-slate-400"
                          }`}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                      <input 
                        type="text" 
                        name="location"
                        value={profileData.location}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none ${
                          isEditing 
                            ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 shadow-sm" 
                            : "bg-slate-50 border-transparent text-slate-500 dark:text-slate-400"
                        }`}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                      <input 
                        type="text" 
                        name="company"
                        value={profileData.company}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none ${
                          isEditing 
                            ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 shadow-sm" 
                            : "bg-slate-50 border-transparent text-slate-500 dark:text-slate-400"
                        }`}
                      />
                    </div>
                    
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Bio / About</label>
                      <textarea 
                        name="bio"
                        value={profileData.bio}
                        onChange={handleChange}
                        disabled={!isEditing}
                        rows={4}
                        className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none resize-none ${
                          isEditing 
                            ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 shadow-sm" 
                            : "bg-slate-50 border-transparent text-slate-500 dark:text-slate-400"
                        }`}
                      />
                    </div>
                  </div>

                  {isEditing && (
                    <div className="mt-8 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-700 pt-6">
                      <button 
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="px-6 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        className="px-6 py-2.5 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                      >
                        <Save size={16} /> Save Changes
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
